import { useAsyncData } from '#imports'
import { computed, watch } from 'vue'

import type { Image } from '@backmarket/http-api/src/api-specs-content/models/image'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import type { Location } from '@ds/types/Location'

import { useRouteLocationWithLocale } from '~/composables/useRouteLocationWithLocale'

import { useCurrentCategory } from '../../composables/useCurrentCategory'
import { MODELS_COMPARISON } from '../../routes-names'

export type ModelsComparisonCategory = {
  image: Image
  title: string
  trackingName: string
  link: Location
}

export function useFetchSuggestedCategories() {
  const $httpFetch = useHttpFetch()
  const resolveLocalizedRoute = useRouteLocationWithLocale()
  const currentCategory = useCurrentCategory()
  const logger = useLogger()

  const { data, error } = useAsyncData(
    'model-comparison-categories',
    () => $httpFetch('/bm/models-comparison/categories'),
    {
      dedupe: 'defer',
      getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
    },
  )

  watch(error, () => {
    if (!error.value) return

    logger.error('[useFetchSuggestedCategories] Failed to fetch', {
      error: error.value,
      owners: ['bot-squad-content-front'],
    })
  })

  const suggestedCategories = computed<ModelsComparisonCategory[]>(() => {
    const allCategories = data.value === undefined ? [] : data.value.categories

    return allCategories
      .filter(({ name }) => name !== currentCategory)
      .map((category) => ({
        image: category.image,
        title: category.label,
        link: resolveLocalizedRoute({
          type: 'internal',
          name: MODELS_COMPARISON.CATEGORY,
          params: { category: category.name },
        }),
        trackingName: category.name,
      }))
  })

  const hasSuggestedCategories = computed(
    () => suggestedCategories.value?.length > 0,
  )

  return { suggestedCategories, hasSuggestedCategories }
}
